import {
    ProductAvailability,
    type Asset,
    type CurrencyCode,
    type ProductFieldsFragment,
    type ProductLabelFieldsFragment,
    type ProductPreviewFieldsFragment,
    type ProductVariantFieldsFragment,
} from '~/graphql/generated';

const getProductImage = (
    product: {
        options: CMSProduct['options'];
        variants: CMSProduct['variants']['items'];
    },
    defaultImage = '',
) => {
    let productImage = '';
    const colorOptionKey = Object.keys(product.options ?? {}).find((option) => option.includes('Color'));
    if (colorOptionKey && product.options?.[colorOptionKey]) {
        for (const color of product.options[colorOptionKey]) {
            const variant = product.variants.find((variant) => variant.selectedOptions?.[colorOptionKey]?.includes(color));
            if (!variant?.image) continue;
            productImage = variant.image;
            break;
        }
    }
    return productImage || product.variants[0]?.image || defaultImage;
};

export const isProductPreviewType = (product: ProductFieldsFragment | ProductPreviewFieldsFragment): product is ProductPreviewFieldsFragment =>
    'content' in product;

export const productAttributesToRemove: (string | number)[] = ['Edition', 'Revision'];

export const isProductAttributeToRemove = (attributeName: string) =>
    productAttributesToRemove.some((attribute) => attributeName === attribute || attributeName.includes(`(${attribute})`));

const parseVariants = (
    title: string,
    types: string[],
    defaultImage: string | undefined,
    variants: (ProductVariantFieldsFragment | null)[] | undefined | null,
    isoLocale: string,
) => {
    const normalizedVariants: CMSProductVariant[] = [];
    const productOptions: CMSProduct['options'] = {};
    const optionsSequence: CMSProduct['optionsSequence'] = [];

    let currencyCode;
    let minPrice = -1;
    if (Array.isArray(variants)) {
        for (const variant of variants) {
            if (!variant || variant.hidden) continue;

            // normalize the selected product options
            const selectedOptions: CMSProductVariant['selectedOptions'] = {};
            for (const selectedOption of variant?.selectedOptions ?? []) {
                if (!selectedOption?.name || !selectedOption.value) continue;
                if (isProductAttributeToRemove(selectedOption.name)) continue;
                if (!optionsSequence.includes(selectedOption.name)) {
                    optionsSequence.push(selectedOption.name);
                }
                if (!productOptions[selectedOption.name]) {
                    productOptions[selectedOption.name] = [selectedOption.value];
                } else if (!productOptions[selectedOption.name].includes(selectedOption.value)) {
                    productOptions[selectedOption.name].push(selectedOption.value);
                }
                if (selectedOption.name === 'Title' && selectedOption.value === DEFAULT_TITLE) {
                    continue;
                }
                selectedOptions[selectedOption.name] = selectedOption.value;
            }
            const idx = variant.id.lastIndexOf(':');
            const hash = variant.id && idx > 0 ? variant.id.substring(idx + 1) : null;
            const variantId = hash ? getNativeVariantId(hash) : undefined;
            const shopTitle = variant.shopTitle !== DEFAULT_TITLE ? variant.shopTitle : '';
            const vTitle = shopTitle !== '' ? `${title} - ${shopTitle}` : title;
            const shopImage = variant.shopImage !== '' ? variant.shopImage : undefined;
            const image = isProductBike({ types }) && variant.sku !== '' ? getAssetUrl(`${variant.sku}-side`) : defaultImage;
            const priceNumber = variant.price?.amount ? Number.parseFloat(variant.price.amount) * 100 : 0;
            const amount = variant.price?.amount ? Number.parseFloat(variant.price.amount) : 0;
            const compareAt = variant.compareAtPrice?.amount ? Number.parseFloat(variant.compareAtPrice.amount) : 0;
            // define the minimum price to be displayed on the product
            if (priceNumber > 0 && (priceNumber < minPrice || minPrice === -1)) {
                minPrice = priceNumber;
                currencyCode = variant.price?.currencyCode;
            }
            const normalizedVariant: CMSProductVariant = {
                id: variant.id,
                nativeId: variant.nativeId as string,
                variantId,
                sku: variant.sku as string,
                availabilityState: variant.availabilityState,
                image,
                shopImage: shopImage as string,
                title: vTitle,
                priceNumeric: priceNumber,
                currency: currencyCode as CurrencyCode,
                price:
                    variant.price?.amount && variant.price?.amount !== ''
                        ? new Intl.NumberFormat(isoLocale, { style: 'currency', currency: variant.price.currencyCode }).format(
                              Number(variant.price.amount),
                          )
                        : undefined,
                compareAtPrice:
                    variant.compareAtPrice?.amount && variant.compareAtPrice?.amount !== '' && amount < compareAt
                        ? new Intl.NumberFormat(isoLocale, { style: 'currency', currency: variant.compareAtPrice.currencyCode }).format(
                              Number(variant.compareAtPrice.amount),
                          )
                        : undefined,
                canBeOrdered: variant.availabilityState === ProductAvailability.Available,
                redCharity: variant.redCharity,
                notifyMe: variant.notifyMe,
                availabilityDate: variant.availabilityDate ? new Date(variant.availabilityDate) : undefined,
                selectedOptions,
                // TODO: check if we can find a better way than a type cast
                labels: variant.labels as ProductLabelFieldsFragment[],
                quantityAvailable: variant.quantityAvailable ?? 0,
            };
            normalizedVariants.push(normalizedVariant);
        }
    }
    // remove the default title option on single variants
    if (productOptions.Title && productOptions.Title.length === 1 && productOptions.Title[0] === DEFAULT_TITLE) {
        delete productOptions.Title;
        if (optionsSequence.includes('Title')) {
            optionsSequence.splice(optionsSequence.indexOf('Title'), 1);
        }
    }
    if (minPrice > 0) {
        minPrice = Math.round(minPrice) / 100;
    }

    return {
        variants: normalizedVariants,
        productOptions,
        optionsSequence,
        minPrice,
        currencyCode,
    };
};

export const canVariantQuickSell = (prod?: CMSProduct, variantId?: string) => {
    if (!variantId) return false;
    if (!prod) return false;
    if (prod.customNaming) return false;
    // custom drop down for custom attribute
    const variant = prod.variants.items.find((v: CMSProductVariant) => v.id === variantId);
    if (variant) {
        return variant.availabilityState === ProductAvailability.Available;
    }
    return false;
};

export const filterProductColor = <T extends ProductFieldsFragment | ProductPreviewFieldsFragment>(items?: T[], colorFilter?: string) => {
    if (!colorFilter) return items;

    const fallbackColor = '*';

    return items?.reduce((newItems, currentItem) => {
        const colorOptionIndex = currentItem.options?.findIndex((option) => option?.name?.includes('Color')) ?? -1;
        if (colorOptionIndex < 0 || !currentItem.options || !currentItem.options[colorOptionIndex]) return newItems;

        // Filter color options on product
        currentItem.options[colorOptionIndex].values = currentItem.options[colorOptionIndex].values?.filter((color) =>
            colorFilter.includes(color ?? fallbackColor),
        );

        // Update variants
        const variants = currentItem.variants?.items?.filter((variant) =>
            colorFilter.includes(variant?.selectedOptions?.find((option) => option?.name?.includes('Color'))?.value ?? fallbackColor),
        );

        newItems.push({
            ...currentItem,
            variants: variants ? { items: variants, total: variants.length } : null,
        });
        return newItems;
    }, [] as T[]);
};

/**
 * This function handles all the differences in the CMSProduct conversion between ProductFieldsFragment | ProductPreviewFieldsFragment
 */
const getCMSProductBaseInfo = (product: ProductFieldsFragment | ProductPreviewFieldsFragment) => {
    if (isProductPreviewType(product)) {
        return {
            productTitle: product.content?.title,
            types: product.content?.types || ['Undefined'],
            defaultImage: getDefaultImage(product.content?.overview),
            productDescription: product.content?.description,
            category: product.content?.category || [],
            carouselDefaultImage: getDefaultImage(product.content?.overview),
            url: product.content?.url?.url,
        };
    }
    return {
        productTitle: product.title,
        types: product.types || ['Undefined'],
        defaultImage: getDefaultImage(product.shopImage),
        productDescription: product.description,
        category: product.category || [],
    };
};

export const getDefaultImage = (image: string | Asset | null | undefined): string | undefined => {
    if (!image) return;
    if (typeof image === 'string') return image;
    if (typeof image === 'object') {
        if (isAssetKontentAi(image)) {
            return image.data.src || undefined;
        } else if (isAssetBynder(image)) {
            return `${image.data.baseUrl || ''}${image.data['1to1']}`;
        }
    }
};

const getNativeGenericId = (id: string | null | undefined, identifier: string) => {
    if (!id) return;
    const parsedId = id.startsWith(identifier) || /^\d+$/.test(id) ? id : atob(id);
    return parsedId.substring(identifier.length);
};

export const getNativeProductId = (id: string | null | undefined) => {
    return getNativeGenericId(id, 'gid://shopify/Product/');
};

export const getNativeVariantId = (id: string | null | undefined) => {
    return getNativeGenericId(id, 'gid://shopify/ProductVariant/');
};

export const isProductBike = (product: { types?: string[] | null } | undefined | null = {}) => Boolean(product?.types?.includes('Bike'));

export const parseTags = (product: ProductFieldsFragment | ProductPreviewFieldsFragment): string[] => {
    const tags: string[] = [];
    if (product.tags) {
        for (const tag of product.tags) {
            if (tag) {
                tags.push(tag);
            }
        }
    }
    return tags;
};

export const processApiProduct = (product: ProductFieldsFragment | ProductPreviewFieldsFragment, isoLocale: string) => {
    const { carouselDefaultImage, category, defaultImage, productTitle, productDescription, types, url } = getCMSProductBaseInfo(product);

    const title = product.shopTitle || 'Undefined Title';
    const formattedTitle = productTitle || title;
    const description = productDescription || '';

    // filter variants that are hidden
    const { variants, productOptions, optionsSequence, minPrice, currencyCode } = parseVariants(
        title,
        types,
        defaultImage,
        product.variants?.items,
        isoLocale,
    );
    const price = minPrice > 0 ? new Intl.NumberFormat(isoLocale, { style: 'currency', currency: currencyCode }).format(minPrice) : undefined;

    const productImage = getProductImage(
        {
            options: productOptions,
            variants,
        },
        carouselDefaultImage,
    );

    // categoryID spare parts: 5637179834
    const showReviews = !product.product_category?.some(({ id }) => id === '5637179834');

    return {
        id: product.id as string,
        nativeId: product.nativeId as string,
        productId: getNativeProductId(product.nativeId),
        handle: product.handle as string,
        title,
        formattedTitle,
        description,
        price,
        priceNumeric: minPrice,
        currency: currencyCode as CurrencyCode,
        types,
        category,
        showReviews,
        productType: product.productType || 'Undefined',
        labels: product.labels as ProductLabelFieldsFragment[],
        limit: product.limit as number,
        tags: parseTags(product),
        url,
        image: productImage,
        // from variant parsing
        options: productOptions,
        optionsSequence,
        variants: {
            total: variants.length,
            items: variants,
        },
        // boolean values
        isGiftCard: false,
        customNaming: product.customNaming,
    } as CMSProduct;
};

export const processApiCollection = (apiProducts: ProductPreviewFieldsFragment[], isoLocale: string, bundleUsage: boolean = false): CMSProduct[] => {
    const ret: CMSProduct[] = [];
    for (const product of apiProducts) {
        if (!product) continue;
        const item = processApiProduct(product, isoLocale);
        if (item) {
            ret.push(item);
        }
    }
    return bundleUsage ? ret : ret.filter((p: CMSProduct) => p.url && p.url !== '');
};
